<script>
    import { saveFiles, excludeFile } from '@/api/file.js';
    import { findFilterGroupByName } from '@/scripts/sortFilesByAssetType.mjs';
    import constants from '@/constants/index.js';
    import { pruneOrphanFilters } from '@/scripts/pruneOrphanFilters.mjs';
    import { TIPS } from './DisputeBulkBar.vue';

    // The folder on Drive and the vision model disagreed on the asset type.
    // Either button is a Save: PATCH /admin with the filters clears the dispute
    // on the backend, so the row leaves the list once the response is back.
    export default {
        props: {
            file: Object,
            expanded: Boolean,
            toggleExpanded: Function,
            functionUpdatePage: Function,
        },
        computed: {
            assetTypeID() { return this.$store.getters.ASSET_TYPE_IMAGES },
            schema() { return this.$store.getters.FILTER_BY_ASSET_TYPE(this.assetTypeID) },
            group() {
                return findFilterGroupByName(this.schema, constants.ASSET_TYPE_FILTER_NAME);
            },
            folderName() { return this.itemName(this.group == null ? null : this.file.filter[this.group.id]) },
            modelName() { return this.itemName(this.file.modelAssetTypeId) },
            modelKnown() { return /^[?#]/.test(this.modelName) === false },
            selected() { return this.$store.getters.SELECTED_IDS.includes(this.file.id) },
            busy() { return this.$store.getters.BUSY_IDS.includes(this.file.id) },
        },
        data() {
            return { erasing: false, TIPS };
        },
        methods: {
            // Not the file-edit bin: rows and S3 objects go for good, and the
            // portal is told never to upload this path again (869f1k9b0).
            async erase() {
                if (!confirm(`Удалить ${this.file.name} навсегда? Файл уйдёт из каталога и S3, GUP его больше не загрузит.`)) return;
                this.erasing = true;
                try {
                    const out = await excludeFile(this.assetTypeID, this.file.providerId, this.file.id);
                    if (out == null) return;
                    if (out.images === 0) { alert('Бэкенд ничего не удалил: файл не найден или не портальный.'); return; }
                    if ((out.unremembered_ids || []).length) alert('Удалено, но у файла нет пути в зеркале: GUP может залить его снова.');
                    this.$store.commit('IMAGE_DELETE_BY_ID', this.file.id);
                    if (this.functionUpdatePage != null) await this.functionUpdatePage();
                } finally {
                    this.erasing = false;
                }
            },
            itemName(id) {
                if (this.group == null || id == null) return '?';
                const item = this.group.filter_items.find((i) => i.id === id);
                return item == null ? `#${id}` : item.name;
            },
            // Same overlay file-edit uses: the original is fetched on demand there,
            // so the list itself never loads more than the 512px preview.
            async showOriginal() {
                this.$store.commit('SELECT_FILE', this.file);
                this.$store.commit('IS_SHOW_BIG_PREVIEW', true);
            },
            async modelIsRight() {
                const before = [...this.file.selectFilter];
                this.file.updateFilter(this.group.id, this.file.modelAssetTypeId, true, false);
                // Characters carries child groups (Framing, Humanism, ...); their
                // items point at the Characters item and the backend refuses the
                // set once it is gone. file-edit prunes them through the editor's
                // watchers, which are not mounted on a folded row.
                const pruned = pruneOrphanFilters(this.file.selectFilter, this.schema);
                if (pruned.length !== this.file.selectFilter.length) {
                    await this.file.replaceSelectFilter(pruned, this.schema, false);
                }
                const saved = await this.save();
                // axiosRequest has already shown the red popup; put everything
                // back so the row does not sit there half-edited.
                if (saved === false) await this.file.replaceSelectFilter(before, this.schema, false);
            },
            async save() {
                // parseImageToSave only sends edited files; a Save with the same
                // filters is still the admin taking a side. The row is dropped by
                // ImageObject once the response clears modelAssetTypeId.
                await this.file.markEdited(true);
                const response = await saveFiles(this.file, this.assetTypeID);
                if (response.length === 0) {
                    await this.file.checkState();
                    return false;
                }
                await this.file.updateFileData(response[0]);
                return true;
            }
        }
    }
</script>


<template>
    <div class="dispute_bar" :class="{ selected, busy }">
        <input
            type="checkbox"
            class="dispute_check"
            :checked="selected"
            :title="TIPS.check"
            @change="$store.commit('TOGGLE_SELECTED', file.id)"
        >
        <img
            class="dispute_preview cursor_pointer"
            :src="file.imagePreview"
            :alt="file.name"
            :title="TIPS.preview"
            @click="showOriginal"
        >
        <div class="dispute_text">
            <div class="dispute_name">[{{ file.id }}] {{ file.name }}</div>
            <div>папка: <b>{{ folderName }}</b> / модель: <b>{{ modelName }}</b></div>
            <div v-if="file.gameName" class="dispute_game">{{ file.gameName }}</div>
        </div>
        <span v-if="busy" class="dispute_busy"><span class="spin"></span>выполняется…</span>
        <template v-else>
            <button class="button_blue_150" :title="TIPS.folder" @click="save">Папка права</button>
            <button class="button_blue_150 ml_10" :disabled="modelKnown === false" :title="TIPS.model" @click="modelIsRight">Модель права</button>
            <button
                class="button_blue_150 button_red ml_10"
                :disabled="erasing || file.providerId == null"
                :title="TIPS.del"
                @click="erase"
            >{{ erasing ? '…' : 'Удалить' }}</button>
            <button class="button_blue_150 ml_10" :title="TIPS.props" @click="toggleExpanded">
                {{ expanded ? 'Свернуть' : 'Все свойства' }}
            </button>
        </template>
    </div>
</template>


<style scoped>
    .dispute_bar {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 8px 12px;
        border-radius: 5px;
        transition: background 0.12s linear;
    }
    .dispute_bar.selected {
        background: color-mix(in srgb, var(--main-color) 12%, var(--background-secondary));
        box-shadow: inset 0 0 0 2px var(--main-color);
    }
    .dispute_bar.busy { opacity: 0.7; }
    .dispute_game { font-size: 12px; color: var(--text-color-secondary); margin-top: 2px; }
    .dispute_busy { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: bold; color: var(--main-color); }
    .spin {
        width: 14px; height: 14px;
        border: 2px solid var(--main-color); border-top-color: transparent;
        border-radius: 50%;
        animation: dispute_spin 0.8s linear infinite;
    }
    @keyframes dispute_spin { to { transform: rotate(360deg); } }
    .dispute_check {
        width: 18px;
        height: 18px;
        cursor: pointer;
        flex-shrink: 0;
    }
    .dispute_preview {
        width: 96px;
        height: 96px;
        object-fit: contain;
        background-color: var(--background);
    }
    .dispute_text {
        flex: 1;
    }
    .dispute_name {
        font-weight: bold;
    }
</style>
